import React, { useCallback, useState } from 'react';
import { Activity, Save, RefreshCw, Download, ChevronDown, ChevronUp, HelpCircle, Info } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicParametersSection from './BasicParametersSection';
import MeasurementsSection from './MeasurementsSection';
import AdvancedParametersSection from './AdvancedParametersSection';
import SkinfoldMeasurementsSection from './SkinfoldMeasurementsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { 
  calculateBodyFatPercentage, 
  calculateNavyBodyFat, 
  calculateJacksonPollockBodyFat,
  calculateBodyComposition 
} from '../../../../../utils/calculations/health';

/**
 * Компонент основной формы расчета процента жира в организме
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  activityLevels,
  measurementMethods,
  saveMeasurement,
  showAdvancedOptions,
  setShowAdvancedOptions
}) => {
  
  // Состояние для имени сохраняемого измерения
  const [saveMeasurementName, setSaveMeasurementName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = 0) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = 0) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Если изменен пол, сбросим значение для бедер для мужчин
      if (name === 'gender' && value === 'male') {
        newState.hip = '0';
      }
      
      // Если изменен метод измерения
      if (name === 'measurementMethod') {
        // Включаем режим калиперометрии для методов складок
        if (['skinfold', 'jackson_pollock_3', 'jackson_pollock_7'].includes(value)) {
          newState.bodyfatSkinfold = true;
        } else {
          newState.bodyfatSkinfold = false;
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      gender, age, weight, height, neck, waist, hip,
      measurementMethod, bodyfatSkinfold, 
      chest, abdomen, thigh, tricep, subscapular, suprailiac, midaxillary
    } = formData;

    // Парсинг числовых значений
    const numAge = parseIntOrDefault(age, -1);
    const numWeight = parseFloatOrDefault(weight, -1);
    const numHeight = parseFloatOrDefault(height, -1);
    const numNeck = parseFloatOrDefault(neck, -1);
    const numWaist = parseFloatOrDefault(waist, -1);
    const numHip = parseFloatOrDefault(hip, -1);

    // Валидация базовых полей
    if (numAge <= 0) newErrors.age = 'Возраст должен быть положительным числом.';
    if (!newErrors.age && numAge > 100) newErrors.age = 'Возраст не может быть больше 100 лет.';
    
    if (numWeight <= 0) newErrors.weight = 'Вес должен быть положительным числом.';
    if (!newErrors.weight && numWeight > 300) newErrors.weight = 'Вес не может быть больше 300 кг.';
    
    if (numHeight <= 0) newErrors.height = 'Рост должен быть положительным числом.';
    if (!newErrors.height && numHeight > 250) newErrors.height = 'Рост не может быть больше 250 см.';

    // Валидация для метода ВМС (Navy)
    if (measurementMethod === 'navy') {
      if (numNeck <= 0) newErrors.neck = 'Окружность шеи должна быть положительным числом.';
      if (numWaist <= 0) newErrors.waist = 'Окружность талии должна быть положительным числом.';
      if (gender === 'female' && numHip <= 0) newErrors.hip = 'Окружность бедер должна быть положительным числом.';
    }

    // Валидация для методов калиперометрии
    if (bodyfatSkinfold) {
      if (measurementMethod === 'jackson_pollock_3' || measurementMethod === 'jackson_pollock_7') {
        const numChest = parseFloatOrDefault(chest, -1);
        const numAbdomen = parseFloatOrDefault(abdomen, -1);
        const numThigh = parseFloatOrDefault(thigh, -1);
        
        if (numChest < 0) newErrors.chest = 'Значение должно быть положительным числом.';
        if (numAbdomen < 0) newErrors.abdomen = 'Значение должно быть положительным числом.';
        if (numThigh < 0) newErrors.thigh = 'Значение должно быть положительным числом.';
        
        // Дополнительные складки для 7-точечного метода
        if (measurementMethod === 'jackson_pollock_7') {
          const numTricep = parseFloatOrDefault(tricep, -1);
          const numSubscapular = parseFloatOrDefault(subscapular, -1);
          const numSuprailiac = parseFloatOrDefault(suprailiac, -1);
          const numMidaxillary = parseFloatOrDefault(midaxillary, -1);
          
          if (numTricep < 0) newErrors.tricep = 'Значение должно быть положительным числом.';
          if (numSubscapular < 0) newErrors.subscapular = 'Значение должно быть положительным числом.';
          if (numSuprailiac < 0) newErrors.suprailiac = 'Значение должно быть положительным числом.';
          if (numMidaxillary < 0) newErrors.midaxillary = 'Значение должно быть положительным числом.';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета процента жира и состава тела
  const calculateBodyFat = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const {
          gender, age, weight, height, neck, waist, hip,
          measurementMethod, activityLevel,
          chest, abdomen, thigh, tricep, subscapular, suprailiac, midaxillary
        } = formData;

        // Парсинг числовых значений
        const numAge = parseIntOrDefault(age);
        const numWeight = parseFloatOrDefault(weight);
        const numHeight = parseFloatOrDefault(height);
        const numNeck = parseFloatOrDefault(neck);
        const numWaist = parseFloatOrDefault(waist);
        const numHip = parseFloatOrDefault(hip);
        
        // Парсинг значений кожных складок
        const skinfolds = {
          chest: parseFloatOrDefault(chest),
          abdomen: parseFloatOrDefault(abdomen),
          thigh: parseFloatOrDefault(thigh),
          tricep: parseFloatOrDefault(tricep),
          subscapular: parseFloatOrDefault(subscapular),
          suprailiac: parseFloatOrDefault(suprailiac),
          midaxillary: parseFloatOrDefault(midaxillary)
        };

        // Данные для расчёта уровня активности
        const activityFactor = activityLevels.find(level => level.id === activityLevel)?.factor || 1.55;

        // --- Расчет процента жира в зависимости от выбранного метода ---
        let bodyFatPercentage = 0;
        let bodyFatMethod = '';
        
        switch(measurementMethod) {
          case 'navy':
            bodyFatPercentage = calculateNavyBodyFat(gender, numHeight, numWeight, numNeck, numWaist, numHip);
            bodyFatMethod = 'Метод ВМС США (окружности)';
            break;
          case 'jackson_pollock_3':
            bodyFatPercentage = calculateJacksonPollockBodyFat(
              gender, 
              numAge, 
              [skinfolds.chest, skinfolds.abdomen, skinfolds.thigh], 
              3
            );
            bodyFatMethod = 'Метод Джексона-Поллока (3 точки)';
            break;
          case 'jackson_pollock_7':
            bodyFatPercentage = calculateJacksonPollockBodyFat(
              gender, 
              numAge, 
              [
                skinfolds.chest, 
                skinfolds.abdomen, 
                skinfolds.thigh,
                skinfolds.tricep,
                skinfolds.subscapular,
                skinfolds.suprailiac,
                skinfolds.midaxillary
              ], 
              7
            );
            bodyFatMethod = 'Метод Джексона-Поллока (7 точек)';
            break;
          default:
            bodyFatPercentage = calculateBodyFatPercentage(
              gender, 
              numAge, 
              numHeight, 
              numWeight, 
              numNeck, 
              numWaist, 
              numHip
            );
            bodyFatMethod = 'Метод ВМС США (окружности)';
        }

        // --- Расчет состава тела ---
        const bodyComposition = calculateBodyComposition(numWeight, bodyFatPercentage);

        // --- Расчет метаболизма ---
        // Базовый метаболизм по формуле Миффлина-Сан Жеора
        let bmr;
        if (gender === 'male') {
          bmr = 10 * numWeight + 6.25 * numHeight - 5 * numAge + 5;
        } else {
          bmr = 10 * numWeight + 6.25 * numHeight - 5 * numAge - 161;
        }

        // Общий расход калорий с учетом активности
        const tdee = Math.round(bmr * activityFactor);

        // Диапазоны для похудения и набора мышечной массы
        const caloricDeficit = Math.round(tdee * 0.85); // 15% дефицит
        const caloricSurplus = Math.round(tdee * 1.1); // 10% профицит

        // Определение оптимального процента жира по полу и возрасту
        let optimalRange = {};
        
        if (gender === 'male') {
          if (numAge < 30) {
            optimalRange = { min: 8, max: 15, athletic: 8, fitness: 12, average: 18, obese: 25 };
          } else if (numAge < 50) {
            optimalRange = { min: 10, max: 18, athletic: 10, fitness: 14, average: 20, obese: 28 };
          } else {
            optimalRange = { min: 12, max: 20, athletic: 12, fitness: 16, average: 22, obese: 30 };
          }
        } else {
          if (numAge < 30) {
            optimalRange = { min: 15, max: 23, athletic: 15, fitness: 19, average: 25, obese: 32 };
          } else if (numAge < 50) {
            optimalRange = { min: 18, max: 26, athletic: 18, fitness: 22, average: 28, obese: 35 };
          } else {
            optimalRange = { min: 20, max: 28, athletic: 20, fitness: 24, average: 30, obese: 38 };
          }
        }

        // Определение категории процента жира
        let category;
        if (bodyFatPercentage <= optimalRange.athletic) {
          category = 'athletic';
        } else if (bodyFatPercentage <= optimalRange.fitness) {
          category = 'fitness';
        } else if (bodyFatPercentage <= optimalRange.average) {
          category = 'average';
        } else if (bodyFatPercentage <= optimalRange.obese) {
          category = 'overweight';
        } else {
          category = 'obese';
        }
        
        // Формируем результаты
        const calculatedResults = {
          weight: numWeight,
          height: numHeight,
          bmi: numWeight / Math.pow(numHeight/100, 2),
          bodyFatPercentage,
          bodyFatMethod,
          bodyComposition,
          metabolic: {
            bmr,
            tdee,
            caloricDeficit,
            caloricSurplus
          },
          optimalRange,
          category,
          gender,
          age: numAge,
          activityLevel,
          activityFactor,
          measurements: {
            neck: numNeck,
            waist: numWaist,
            hip: numHip,
            skinfolds: { ...skinfolds }
          }
        };
        
        setResults(calculatedResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, activityLevels]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      gender: 'male',
      age: '30',
      weight: '75',
      height: '175',
      neck: '37',
      waist: '85',
      hip: '0', // Для женщин
      activityLevel: 'moderate',
      measurementMethod: 'navy',
      bodyfatSkinfold: false,
      chest: '0',
      abdomen: '0',
      thigh: '0',
      tricep: '0',
      subscapular: '0',
      suprailiac: '0',
      midaxillary: '0'
    });
    setResults(null);
    setErrors({});
    setSaveMeasurementName('');
    setShowSaveDialog(false);
    setShowAdvancedOptions(false);
  };

  // Обработчик сохранения измерения
  const handleSaveMeasurement = () => {
    if (showSaveDialog) {
      const savedResult = saveMeasurement(saveMeasurementName || null);
      if (savedResult) {
        alert(`Измерение "${savedResult.name}" успешно сохранено!`);
      }
      setShowSaveDialog(false);
      setSaveMeasurementName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Функция экспорта результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Форматирование числа с десятичной точкой
    const formatCsvNumber = (num) => typeof num === 'number' ? num.toFixed(2).replace('.', ',') : '';

    // Основная информация результатов
    const data = [
      ['Параметр', 'Значение'],
      ['Пол', results.gender === 'male' ? 'Мужской' : 'Женский'],
      ['Возраст', results.age],
      ['Рост (см)', results.height],
      ['Вес (кг)', results.weight],
      ['Индекс массы тела (ИМТ)', formatCsvNumber(results.bmi)],
      ['Процент жира в организме (%)', formatCsvNumber(results.bodyFatPercentage)],
      ['Метод расчета', results.bodyFatMethod],
      ['Масса жира (кг)', formatCsvNumber(results.bodyComposition.fatMass)],
      ['Безжировая масса (кг)', formatCsvNumber(results.bodyComposition.leanMass)],
      ['Базовый метаболизм (BMR, ккал)', results.metabolic.bmr],
      ['Ежедневный расход калорий (TDEE, ккал)', results.metabolic.tdee],
      ['Рекомендуемый диапазон для похудения (ккал)', results.metabolic.caloricDeficit],
      ['Рекомендуемый диапазон для набора массы (ккал)', results.metabolic.caloricSurplus],
      ['Оптимальный диапазон процента жира (%)', `${results.optimalRange.min}%-${results.optimalRange.max}%`],
      ['Измерения'],
      ['Окружность шеи (см)', results.measurements.neck],
      ['Окружность талии (см)', results.measurements.waist]
    ];

    // Добавляем окружность бедер для женщин
    if (results.gender === 'female') {
      data.push(['Окружность бедер (см)', results.measurements.hip]);
    }
    
    // Добавляем данные складок, если использовался метод калиперометрии
    if (results.bodyFatMethod.includes('Джексона-Поллока')) {
      data.push(['Измерения складок (мм)']);
      data.push(['Грудь', formatCsvNumber(results.measurements.skinfolds.chest)]);
      data.push(['Живот', formatCsvNumber(results.measurements.skinfolds.abdomen)]);
      data.push(['Бедро', formatCsvNumber(results.measurements.skinfolds.thigh)]);
      
      // Для 7-точечного метода
      if (results.bodyFatMethod.includes('7 точек')) {
        data.push(['Трицепс', formatCsvNumber(results.measurements.skinfolds.tricep)]);
        data.push(['Лопатка', formatCsvNumber(results.measurements.skinfolds.subscapular)]);
        data.push(['Надвздошная', formatCsvNumber(results.measurements.skinfolds.suprailiac)]);
        data.push(['Подмышечная', formatCsvNumber(results.measurements.skinfolds.midaxillary)]);
      }
    }

    // Формируем CSV
    const csvContent = '\uFEFF' + data.map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'результаты_процента_жира.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateBodyFat();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Activity className="w-6 h-6 text-emerald-500" />
          Расчет процента жира в организме
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Информационный блок */}
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Для наиболее точного расчета процента жира введите свои текущие измерения. 
                Калькулятор поддерживает несколько методов оценки: метод ВМС США (окружности тела) и 
                методы Джексона-Поллока с использованием калипера (измерителя кожных складок).
              </p>
            </div>
          </div>
        </div>

        {/* Основные параметры */}
        <BasicParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          activityLevels={activityLevels}
        />

        {/* Методы измерения */}
        <div className="mb-6 border-t border-gray-200 dark:border-gray-700 pt-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Метод измерения
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {measurementMethods.map((method) => (
              <label 
                key={method.id} 
                className={`flex items-start p-3 border rounded-lg ${
                  formData.measurementMethod === method.id
                    ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-900/20'
                    : 'border-gray-200 dark:border-gray-700'
                } hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors`}
              >
                <input
                  type="radio"
                  name="measurementMethod"
                  value={method.id}
                  checked={formData.measurementMethod === method.id}
                  onChange={handleInputChange}
                  className="form-radio mt-1"
                />
                <div className="ml-3">
                  <span className="block text-sm font-medium text-gray-900 dark:text-white">
                    {method.name}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {method.description}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Параметры измерений */}
        <MeasurementsSection 
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />

        {/* Секция для складок (калиперометрия) */}
        {formData.bodyfatSkinfold && (
          <SkinfoldMeasurementsSection 
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}

        {/* Переключатель показа расширенных опций */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className="inline-flex items-center text-sm text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400"
          >
            {showAdvancedOptions ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
            {showAdvancedOptions ? 'Скрыть расширенные настройки' : 'Показать расширенные настройки'}
          </button>
        </div>

        {/* Расширенные параметры */}
        {showAdvancedOptions && (
          <AdvancedParametersSection 
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            activityLevels={activityLevels}
          />
        )}

        {/* Диалог сохранения измерения */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveMeasurementName}
                onChange={(e) => setSaveMeasurementName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveMeasurement}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Activity className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Вычисляем...' : 'Рассчитать процент жира'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveMeasurement}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить измерение
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;